import type { MouseEvent } from 'react';

import { categoryLog } from '../tracker/log/category';

import { createDefaultProviders } from './defaults';
import { hrefProvider } from './providers/href';
import { applyProviderChain, hasHrefFallback } from './utils';

import type { AdapterEnv, NavigationProvider } from './types';
import type { CategoryWidgetActionMeta } from '@yanolja-org/core-ui';

export interface CategoryActionHandlerConfig {
  /**
   * 현재는 logger / navigate 동작에 env-별 분기 없음 — `createActionHandler` 와의 시그니처 일관성 / 향후 확장 hook 목적.
   */
  env?: AdapterEnv;
  providers?: NavigationProvider[];
}

/**
 * `CategoryDropdown.onAction` 전용 dispatch factory.
 *
 * 흐름:
 *   1. `e.preventDefault()` — a tag default 차단 (await 이전 sync)
 *   2. `await categoryLog.click(meta)` — log flush
 *   3. `meta.action.web` 있으면 provider chain 으로 navigate (nolBridge → href)
 *
 * routes table 안 씀 — URL 이 서버 응답 (meta.action.web) 의 동적 값.
 *
 * @example
 *   const handleCategory = createCategoryActionHandler({ env, providers: [nolBridgeProvider] });
 *   <CategoryDropdown onAction={handleCategory} />
 */
export function createCategoryActionHandler(config: CategoryActionHandlerConfig = {}) {
  const userProviders = config.providers ?? createDefaultProviders();
  // hrefProvider 자동 마지막 fallback — `createActionHandler` 와 동일 정책.
  const providers = hasHrefFallback(userProviders) ? userProviders : [...userProviders, hrefProvider];

  return async (action: string, meta: CategoryWidgetActionMeta, e: MouseEvent) => {
    e.preventDefault();
    await categoryLog.click(meta);

    const url = meta.action?.web;
    if (!url) return;

    // category 의 action 식별자 ('hotel' / 'pension' 등) 그대로 전달 — provider 의 default branch 동작 (openWebView).
    // 향후 category 의 special bridge interface 추가 시 nolBridgeProvider 가 분기 확장.
    applyProviderChain(providers, url, action);
  };
}
